class ShirtCarousel {
  constructor(sectionId) {
    this.section = document.getElementById('shirt-carousel-' + sectionId);
    if (!this.section) return;

    const dataEl = document.getElementById('shirt-data-' + sectionId);
    if (!dataEl) return;

    try {
      this.products = JSON.parse(dataEl.textContent);
    } catch (e) { return; }

    this.count = this.products.length;
    if (this.count === 0) return;

    this.track = this.section.querySelector('.shirts-track');
    this.inner = this.section.querySelector('.shirt-carousel-inner');
    this.stage = this.section.querySelector('.shirt-carousel-stage');
    this.headEl = this.section.querySelector('.character-head-wrapper');
    this.viewBtn = this.section.querySelector('.carousel-btn--view');

    this.allItems = [...this.section.querySelectorAll('.shirt-item')];
    this.buf = 5;
    this.domIndex = this.buf;
    this.isAnimating = false;
    this.queued = 0;
    this.lastTapTime = 0;
    this.slideDuration = 0.45;
    this.dragVelocity = 0;
    this.currentOffset = 0;
    this._itemW = 0;
    this._stageW = 0;
    this._visible = false;
    this._firstVisible = true;

    this._hoveredIndex = -1;
    this._momentumVel = 0; // px/ms, tracked during momentum for compounding
    this._isDizzy = false;
    this._dizzyTimer = null;
    this._fastSpinCount = 0;
    this._lastSpinDir = 0;

    this._measureDims();
    new ResizeObserver(() => { this._measureDims(); if (this._itemW && this._stageW) this.render(false); }).observe(this.stage);

    new IntersectionObserver(([entry]) => {
      this._visible = entry.isIntersecting;
      if (this._visible) {
        if (this._firstVisible) {
          this._firstVisible = false;
          setTimeout(() => { if (this._visible) this.startAutoScroll(); }, 3000);
        } else {
          this.startAutoScroll();
        }
      } else {
        this.stopAutoScroll();
      }
    }, { threshold: 0.1 }).observe(this.section);

    this.section.querySelector('.carousel-btn--prev')
      .addEventListener('click', () => this.navigate(-1));
    this.section.querySelector('.carousel-btn--next')
      .addEventListener('click', () => this.navigate(1));

    this.allItems.forEach((item, i) => {
      item.addEventListener('click', () => {
        const url = item.dataset.url;
        if (url) window.location.href = url;
      });
      item.addEventListener('mouseenter', () => { this._hoveredIndex = i; });
      item.addEventListener('mouseleave', () => {
        if (this._hoveredIndex === i) this._hoveredIndex = -1;
      });
    });

    this.section.addEventListener('mouseleave', () => { this._hoveredIndex = -1; });

    this.initSwipe();
    this.initHeadDrag();
    this.render(false);
    this._preloadAllImages();
  }

  _measureDims() {
    if (!this.allItems[0]) return;
    const item = this.allItems[0];
    const cs = getComputedStyle(item);
    this._itemW = item.getBoundingClientRect().width + parseFloat(cs.marginLeft) + parseFloat(cs.marginRight);
    this._stageW = this.section.offsetWidth;
  }

  _animateTo(targetOffset, duration, onComplete) {
    const startOffset = this.currentOffset;
    let startTime = null;
    cancelAnimationFrame(this._animFrame);
    const smoothstep = t => t * t * (3 - 2 * t);

    const frame = (now) => {
      if (!startTime) startTime = now;
      const t = Math.min((now - startTime) / (duration * 1000), 1);
      const offset = startOffset + (targetOffset - startOffset) * smoothstep(t);
      this.currentOffset = offset;
      this.track.style.transform = `translateX(${offset}px)`;
      this.updateItemStyles(offset);
      if (t < 1) {
        this._animFrame = requestAnimationFrame(frame);
      } else {
        this._animFrame = null;
        this.currentOffset = targetOffset;
        onComplete?.();
      }
    };

    this.track.style.transition = 'none';
    this._animFrame = requestAnimationFrame(frame);
  }

  // Advances one shirt per second — no rAF loop, no continuous motion
  _autoStep() {
    if (this.isAnimating) return;
    if (this._hoveredIndex === this.domIndex) return;
    this.domIndex += 1;

    const w = this._itemW;
    if (!w) return;
    const targetOffset = Math.round(this._stageW / 2 + ShirtCarousel.SHIFT_X - this.domIndex * w - w / 2);

    this.isAnimating = true;
    this._animateTo(targetOffset, this.slideDuration, () => {
      if (this.domIndex >= this.count + this.buf) { this.domIndex -= this.count; this.render(false); }
      this.isAnimating = false;
      this._loadNearbyImages(this.currentOffset);
    });

    this.allItems.forEach((item, i) => item.classList.toggle('is-active', i === this.domIndex));
    if (this.viewBtn) this.viewBtn.href = this.products[this.logicalIndex()].url;
  }

  _setDizzy(shouldBeDizzy) {
    if (shouldBeDizzy) {
      clearTimeout(this._dizzyTimer);
      this._dizzyTimer = null;
      this._applyDizzy(true);
    } else if (this._isDizzy) {
      if (!this._dizzyTimer) {
        this._dizzyTimer = setTimeout(() => {
          this._dizzyTimer = null;
          this._applyDizzy(false);
        }, 800);
      }
    }
  }

  _applyDizzy(dizzy) {
    if (this._isDizzy === dizzy) return;
    const wasDizzy = this._isDizzy;
    this._isDizzy = dizzy;
    const eyeEls = this.section.querySelectorAll('.character-eye:not(.character-eye--blink)');
    const swirls = this.section.querySelector('.character-swirls');
    if (!eyeEls.length || !swirls) return;
    const wrapper = this.section.querySelector('.character-head-wrapper');
    if (dizzy) {
      this.section.dataset.dizzy = '1';
      // Shrink eyes out fast, then spring-pop swirls in
      eyeEls.forEach(el => { el.style.transition = 'transform 0.1s ease-in, opacity 0.1s ease-in'; });
      wrapper && wrapper.style.setProperty('--eye-scale', '0');
      eyeEls.forEach(el => { el.style.opacity = '0'; });
      setTimeout(() => {
        eyeEls.forEach(el => { el.style.transition = ''; });
        swirls.style.opacity = '1';
        wrapper && wrapper.style.setProperty('--swirl-scale', '1');
      }, 120);
    } else {
      swirls.style.transition = 'transform 0.08s ease-in, opacity 0.05s ease';
      swirls.style.opacity = '0';
      wrapper && wrapper.style.setProperty('--swirl-scale', '0');
      wrapper && wrapper.style.setProperty('--eye-scale', '1');
      setTimeout(() => { swirls.style.transition = ''; }, 100);
      if (wasDizzy) {
        this._doPostDizzyBlink(eyeEls);
      } else {
        eyeEls.forEach(el => { el.style.opacity = '1'; });
        delete this.section.dataset.dizzy;
      }
    }
  }

  _doPostDizzyBlink(eyeEls) {
    const blinkEls = this.section.querySelectorAll('.character-eye--blink');
    if (!blinkEls.length) {
      eyeEls.forEach(el => { el.style.opacity = '1'; });
      delete this.section.dataset.dizzy;
      return;
    }

    this.section.dataset.headShake = '1';
    setTimeout(() => delete this.section.dataset.headShake, 650);

    // swirl → blink (same paint frame, no gap)
    eyeEls.forEach(el => { el.style.opacity = '0'; });
    blinkEls.forEach(el => { el.style.opacity = '1'; });

    const f = (fn) => setTimeout(fn, 100);

    f(() => {         // normal
      blinkEls.forEach(el => { el.style.opacity = '0'; });
      eyeEls.forEach(el => { el.style.opacity = '1'; });
      f(() => {       // blink
        eyeEls.forEach(el => { el.style.opacity = '0'; });
        blinkEls.forEach(el => { el.style.opacity = '1'; });
        f(() => {     // normal (stay)
          blinkEls.forEach(el => { el.style.opacity = '0'; });
          eyeEls.forEach(el => { el.style.opacity = '1'; });
          delete this.section.dataset.dizzy;
        });
      });
    });
  }

  startAutoScroll() {
    if (this._autoScroll || !this._visible) return;
    this._autoScroll = setInterval(() => this._autoStep(), 1500);
  }

  stopAutoScroll() {
    clearInterval(this._autoScroll);
    this._autoScroll = null;
  }

  _preloadAllImages() {
    this.allItems.forEach(item => {
      const img = item.querySelector('.shirt-product-img');
      if (!img || img.dataset.loaded || !img.dataset.src) return;
      img.src = img.dataset.src;
      img.dataset.loaded = '1';
    });
  }

  // Only load images for items within visible range — skips off-screen shirts
  _loadNearbyImages(trackOffset) {
    const w = this._itemW;
    if (!w) return;
    const containerCenter = this._stageW / 2;
    this.allItems.forEach((item, i) => {
      const img = item.querySelector('.shirt-product-img');
      if (!img || img.dataset.loaded || !img.dataset.src) return;
      const distItems = Math.abs((trackOffset + i * w + w / 2 - containerCenter) / w);
      if (distItems <= 5) {
        img.src = img.dataset.src;
        img.dataset.loaded = '1';
      }
    });
  }

  initSwipe() {
    let startX = 0;
    let startY = 0;
    let startOffset = 0;
    let isDragging = false;
    let isHorizontal = null;
    let lastX = 0;
    let lastTime = 0;
    let velocity = 0;

    this.section.addEventListener('touchstart', e => {
      const touch = e.touches[0];
      const stageRect = this.stage.getBoundingClientRect();
      const headRect = this.headEl ? this.headEl.getBoundingClientRect() : null;
      const ty = touch.clientY;
      this._inDragZone = (
        (ty >= stageRect.top && ty <= stageRect.bottom) ||
        (headRect && ty >= headRect.top && ty <= headRect.bottom)
      );
      if (!this._inDragZone) return; // outside shirt/head zone — let page scroll

      // Stop all animation immediately so nothing moves under the finger
      this.stopAutoScroll();
      clearTimeout(this._resumeTimer);
      cancelAnimationFrame(this._animFrame);
      this._animFrame = null;
      this.isAnimating = false;
      this._setDizzy(false);
      startX = touch.clientX;
      startY = touch.clientY;
      lastX = startX;
      lastTime = Date.now();
      velocity = 0;
      isDragging = false;
      isHorizontal = null;
    }, { passive: true });

    this.section.addEventListener('touchmove', e => {
      if (!this._inDragZone) return; // outside shirt/head zone — let page scroll freely
      e.preventDefault(); // in drag zone: carousel takes over touch handling
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;

      if (isHorizontal === null) {
        const dx = Math.abs(x - startX);
        const dy = Math.abs(y - startY);
        if (dx < 5 && dy < 5) return;
        isHorizontal = dx >= dy;
        if (!isHorizontal) return; // vertical within drag zone — do nothing
        // Confirmed horizontal: now commit to carousel drag
        this.stopAutoScroll();
        clearTimeout(this._resumeTimer);
        cancelAnimationFrame(this._animFrame);
        this._animFrame = null;
        this.isAnimating = false;
        isDragging = true;
        startOffset = this.currentOffset;
        this.track.style.transition = 'none';
      }

      if (!isDragging) return;

      const now = Date.now();
      const dt = now - lastTime;
      if (dt > 0) velocity = (x - lastX) / dt;
      lastX = x;
      lastTime = now;
      this.dragVelocity = velocity;
      const liveOffset = startOffset + (x - startX);
      this.track.style.transform = `translateX(${liveOffset}px)`;
      this.updateItemStyles(liveOffset);
    }, { passive: false });

    this.section.addEventListener('touchend', e => {
      if (!this._inDragZone) return; // not a carousel touch — ignore
      this._inDragZone = false;
      if (!isDragging) { isHorizontal = null; this.startAutoScroll(); return; }
      isDragging = false;
      isHorizontal = null;
      const draggedOffset = startOffset + (e.changedTouches[0].clientX - startX);
      this.snapToOffset(draggedOffset, velocity + this._momentumVel);
      this.dragVelocity = 0;
    }, { passive: true });

    // ── Mouse drag for desktop ──
    let mouseStartX = 0;
    let mouseStartOffset = 0;
    let isMouseDragging = false;
    let mouseDragged = false;
    let mouseLastX = 0;
    let mouseLastTime = 0;
    let mouseVelocity = 0;

    this.section.addEventListener('mousedown', e => {
      if (e.target.closest('.carousel-btn')) return;
      e.preventDefault();
      this.stopAutoScroll();
      clearTimeout(this._resumeTimer);
      cancelAnimationFrame(this._animFrame);
      this._animFrame = null;
      this.isAnimating = false;
      this._setDizzy(false);
      isMouseDragging = true;
      mouseDragged = false;
      mouseStartX = e.clientX;
      mouseLastX = mouseStartX;
      mouseLastTime = Date.now();
      mouseVelocity = 0;
      mouseStartOffset = this.currentOffset;
      this.track.style.transition = 'none';
      this.section.style.cursor = 'grabbing';
      cancelAnimationFrame(this._animFrame);
      this._animFrame = null;
    });

    document.addEventListener('mousemove', e => {
      if (!isMouseDragging) return;
      const x = e.clientX;
      if (Math.abs(x - mouseStartX) > 4) mouseDragged = true;
      const now = Date.now();
      const dt = now - mouseLastTime;
      if (dt > 0) mouseVelocity = (x - mouseLastX) / dt;
      mouseLastX = x;
      mouseLastTime = now;
      this.dragVelocity = mouseVelocity;
      const mouseLiveOffset = mouseStartOffset + (x - mouseStartX);
      this.track.style.transform = `translateX(${mouseLiveOffset}px)`;
      this.updateItemStyles(mouseLiveOffset);
    });

    document.addEventListener('mouseup', e => {
      if (!isMouseDragging) return;
      isMouseDragging = false;
      this.section.style.cursor = '';
      if (!mouseDragged) {
        this.currentOffset = mouseStartOffset;
        this.render(false);
        return;
      }
      const draggedOffset = mouseStartOffset + (e.clientX - mouseStartX);
      this.snapToOffset(draggedOffset, mouseVelocity + this._momentumVel);
      this.dragVelocity = 0;
    });

    this.section.addEventListener('click', e => {
      if (mouseDragged) {
        e.preventDefault();
        e.stopPropagation();
        mouseDragged = false;
      }
    }, true);
  }

  initHeadDrag() {
    const wrapper = this.section.querySelector('.character-head-wrapper');
    if (!wrapper) return;

    let isDragging = false;
    let startX = 0, startY = 0;
    let dragX = 0, dragY = 0;
    let rawDist = 0;
    let velX = 0, velY = 0;
    let lastX = 0, lastY = 0;
    let lastTime = 0;
    let pendulumFrame = null;
    let dragFrame = null;
    let headCX = 0, headCY = 0;
    let currentRot = 0, targetRot = 0;

    const applyTransform = (x, y, rot) => {
      const r = rot !== undefined ? rot : 6 * Math.tanh(x / 2);
      wrapper.style.transform = `translateX(calc(-50% + ${2 + x}px)) translateY(${y}px) rotate(${r.toFixed(2)}deg)`;
    };

    const runDragLoop = () => {
      currentRot += (targetRot - currentRot) * 0.18;
      const shakeAmp = 1.5;
      const shakeFreq = Math.max(18, 70 - rawDist * 0.5);
      const shakeX = shakeAmp * Math.sin(Date.now() / shakeFreq);
      applyTransform(dragX + shakeX, dragY, currentRot);
      dragFrame = requestAnimationFrame(runDragLoop);
    };

    const onStart = (x, y) => {
      cancelAnimationFrame(pendulumFrame);
      cancelAnimationFrame(dragFrame);
      pendulumFrame = null;
      dragFrame = null;
      wrapper.style.transform = '';
      delete this.section.dataset.headDrag;
      isDragging = false;
      dragX = 0; dragY = 0; rawDist = 0;
      velX = 0; velY = 0;
      currentRot = 0; targetRot = 0;
      startX = x; startY = y;
      lastX = x; lastY = y;
      lastTime = Date.now();
      const rect = wrapper.getBoundingClientRect();
      headCX = rect.left + rect.width / 2;
      headCY = rect.top + rect.height / 2;
    };

    const onMove = (x, y) => {
      const now = Date.now();
      const dt = now - lastTime;
      if (dt > 0) { velX = (x - lastX) / dt; velY = (y - lastY) / dt; }
      lastX = x; lastY = y; lastTime = now;

      if (!isDragging) {
        if (Math.hypot(x - startX, y - startY) > 4) {
          isDragging = true;
          this.section.dataset.headDrag = '1';
          wrapper.dispatchEvent(new CustomEvent('sc:drag-start'));
          dragFrame = requestAnimationFrame(runDragLoop);
        } else return;
      }

      const dx = x - startX, dy = y - startY;
      rawDist = Math.hypot(dx, dy);
      dragX = 0.45 * dx / (1 + Math.abs(dx) / 400);
      dragY = 0.55 * dy / (1 + Math.abs(dy) / 400);
      const angle = Math.atan2(x - headCX, -(y - headCY)) * (180 / Math.PI);
      targetRot = angle * Math.tanh(rawDist / 80) * 0.32;
    };

    const onEnd = () => {
      if (!isDragging) { delete this.section.dataset.headDrag; return; }
      isDragging = false;
      cancelAnimationFrame(dragFrame);
      dragFrame = null;
      this._applyDizzy(true);

      let vx = velX * 1000, vy = velY * 1000;
      let x = dragX, y = dragY;
      let rot = currentRot, vrot = 0;
      const k = 280, c = 14;

      // Guarantee a visible slingshot arc even on slow releases
      const dispMag = Math.hypot(x, y);
      const speed = Math.hypot(vx, vy);
      const minBoost = 150;
      if (speed < minBoost && dispMag > 0.5) {
        const boost = minBoost - speed;
        vx += (x / dispMag) * boost;
        vy += (y / dispMag) * boost;
      }

      let lastT = null;
      const frame = (now) => {
        if (!lastT) lastT = now;
        const dt = Math.min((now - lastT) / 1000, 0.05);
        lastT = now;
        vx += (-k * x - c * vx) * dt;
        vy += (-k * y - c * vy) * dt;
        x += vx * dt;
        y += vy * dt;
        vrot += (-k * rot - c * vrot) * dt;
        rot += vrot * dt;
        applyTransform(x, y, rot);

        if (Math.abs(x) < 0.5 && Math.abs(y) < 0.5 && Math.abs(vx) < 3 && Math.abs(vy) < 3 && Math.abs(rot) < 0.5 && Math.abs(vrot) < 5) {
          this._applyDizzy(false);
          delete this.section.dataset.headDrag;
          wrapper.style.transform = '';
          wrapper.dispatchEvent(new CustomEvent('sc:drag-end'));
          return;
        }
        pendulumFrame = requestAnimationFrame(frame);
      };
      pendulumFrame = requestAnimationFrame(frame);
    };

    // Touch
    wrapper.addEventListener('touchstart', e => {
      e.stopPropagation();
      onStart(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });

    wrapper.addEventListener('touchmove', e => {
      e.preventDefault();
      e.stopPropagation();
      onMove(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: false });

    wrapper.addEventListener('touchend', e => {
      e.stopPropagation();
      onEnd();
    }, { passive: true });

    // Mouse
    wrapper.addEventListener('mousedown', e => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      onStart(e.clientX, e.clientY);
      const move = (e) => onMove(e.clientX, e.clientY);
      const up = () => { onEnd(); document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
    });
  }

  snapToOffset(draggedOffset, velocity) {
    const w = this._itemW;
    if (!w) return;

    // Convert px/ms → px/s, cap at ±2000 for sanity
    let vel = Math.max(-2000, Math.min(2000, velocity * 1000));
    const k = 1.5; // decay constant: velocity reaches ~5% at 2 s
    let lastTime = null;
    let startTime = null;

    this.isAnimating = true;
    this.currentOffset = draggedOffset;
    this.track.style.transition = 'none';
    this.stopAutoScroll();
    clearTimeout(this._resumeTimer);
    cancelAnimationFrame(this._animFrame);
    // Count consecutive fast spins in the same direction; reset on full stop
    const dir = Math.sign(vel);
    if (Math.abs(vel) > 350) {
      if (dir === this._lastSpinDir) {
        this._fastSpinCount++;
      } else {
        this._fastSpinCount = 1;
        this._lastSpinDir = dir;
      }
    } else {
      this._fastSpinCount = 0;
      this._lastSpinDir = 0;
    }

    const settle = () => {
      clearTimeout(this._dizzyTimer);
      this._dizzyTimer = null;
      this._applyDizzy(false);
      this._fastSpinCount = 0;
      this._lastSpinDir = 0;
      this._momentumVel = 0;
      const rawIndex = (this._stageW / 2 + ShirtCarousel.SHIFT_X - w / 2 - this.currentOffset) / w;
      let target = Math.round(rawIndex);
      target = Math.max(0, Math.min(this.count + 2 * this.buf - 1, target));
      this.domIndex = target;
      const targetOffset = Math.round(this._stageW / 2 + ShirtCarousel.SHIFT_X - target * w - w / 2);
      this._animateTo(targetOffset, 0.35, () => {
        if (this.domIndex < this.buf) { this.domIndex += this.count; this.render(false); }
        else if (this.domIndex >= this.count + this.buf) { this.domIndex -= this.count; this.render(false); }
        this.isAnimating = false;
        this._loadNearbyImages(this.currentOffset);
        this.stopAutoScroll();
        this.startAutoScroll();
      });
      this.allItems.forEach((item, i) => item.classList.toggle('is-active', i === this.domIndex));
      if (this.viewBtn) this.viewBtn.href = this.products[this.logicalIndex()].url;
    };

    const frame = (now) => {
      if (!startTime) { startTime = now; lastTime = now; }
      const dt = Math.min((now - lastTime) / 1000, 0.05); // seconds, capped to avoid jumps
      lastTime = now;
      const elapsed = (now - startTime) / 1000;

      vel *= Math.exp(-k * dt);
      this._momentumVel = vel / 1000; // keep in px/ms for compounding
      this.currentOffset += vel * dt;
      if (Math.abs(vel) > 350 && this._fastSpinCount >= 3) {
        this._setDizzy(true);
      } else {
        this._setDizzy(false);
      }

      // Silent infinite wrap during momentum
      const approxIndex = Math.round((this._stageW / 2 + ShirtCarousel.SHIFT_X - w / 2 - this.currentOffset) / w);
      if (approxIndex < this.buf) {
        this.currentOffset -= this.count * w;
      } else if (approxIndex >= this.count + this.buf) {
        this.currentOffset += this.count * w;
      }

      this.track.style.transform = `translateX(${this.currentOffset}px)`;
      this.updateItemStyles(this.currentOffset);

      if (elapsed < 2.0 && Math.abs(vel) > 40) {
        this._animFrame = requestAnimationFrame(frame);
      } else {
        settle();
      }
    };

    this._animFrame = requestAnimationFrame(frame);
  }

  navigate(dir) {
    if (this.isAnimating) {
      this.queued = dir;
      return;
    }

    this.stopAutoScroll();
    clearTimeout(this._resumeTimer);

    const now = Date.now();
    const gap = now - this.lastTapTime;
    this.slideDuration = gap < 500 ? Math.max(0.12, this.slideDuration * 0.6) : 0.45;
    this.lastTapTime = now;

    this.isAnimating = true;
    this.queued = 0;
    this.domIndex += dir;

    const w = this._itemW;
    const targetOffset = Math.round(this._stageW / 2 + ShirtCarousel.SHIFT_X - this.domIndex * w - w / 2);

    this._animateTo(targetOffset, this.slideDuration, () => {
      if (this.domIndex < this.buf) { this.domIndex += this.count; this.render(false); }
      else if (this.domIndex >= this.count + this.buf) { this.domIndex -= this.count; this.render(false); }
      this.isAnimating = false;
      this._loadNearbyImages(this.currentOffset);
      if (this.queued !== 0) {
        this.navigate(this.queued);
      } else {
        this._resumeTimer = setTimeout(() => this.startAutoScroll(), 3000);
      }
    });

    this.allItems.forEach((item, i) => item.classList.toggle('is-active', i === this.domIndex));
    if (this.viewBtn) this.viewBtn.href = this.products[this.logicalIndex()].url;
  }

  logicalIndex() {
    if (this.domIndex < this.buf) return this.count - this.buf + this.domIndex;
    if (this.domIndex >= this.count + this.buf) return this.domIndex - this.count - this.buf;
    return this.domIndex - this.buf;
  }

  updateItemStyles(trackOffset) {
    const w = this._itemW;
    if (!w) return;
    const containerCenter = this._stageW / 2 + ShirtCarousel.SHIFT_X;
    const rate = 0.25;
    const factor = 1 / (1 - rate);

    this.allItems.forEach((item, i) => {
      const itemCenter = trackOffset + i * w + w / 2;
      const relPos = (itemCenter - containerCenter) / w;
      const distItems = Math.abs(relPos);

      if (distItems > 4) {
        if (item.style.opacity !== '0') {
          item.style.opacity = '0';
          item.style.zIndex = '0';
        }
        return;
      }

      const scale = Math.pow(0.72, distItems);
      const opacity = Math.max(0.5, 1 - Math.max(0, distItems - 2) * 0.25);
      const spread = Math.max(1, distItems);
      const arcPull = Math.max(0, distItems - 2) * w * 0.2;
      const compressionX = Math.sign(relPos) * (w * (factor * (1 - Math.pow(rate, spread)) - spread) - arcPull);
      const dur = this.isAnimating
        ? 0
        : Math.max(0, 0.12 - Math.abs(this.dragVelocity) * 0.1).toFixed(3);
      item.style.transition = parseFloat(dur) > 0
        ? `transform ${dur}s ease, opacity ${dur}s ease`
        : 'none';
      item.style.transform = `translateX(${compressionX}px) scale(${scale})`;
      item.style.opacity = opacity;
      item.style.zIndex = Math.round(100 - distItems * 10);
      item.style.cursor = distItems < 1.5 ? 'pointer' : 'default';
    });
  }

  render(animate) {
    if (!this._itemW || !this._stageW) {
      this._measureDims();
      if (!this._itemW || !this._stageW) return;
    }
    const w = this._itemW;
    const offset = Math.round(this._stageW / 2 + ShirtCarousel.SHIFT_X - this.domIndex * w - w / 2);

    if (animate) {
      this._animateTo(offset, this.slideDuration, null);
    } else {
      cancelAnimationFrame(this._animFrame);
      this._animFrame = null;
      this.currentOffset = offset;
      this.track.style.transition = 'none';
      void this.track.offsetWidth;
      this.track.style.transform = `translateX(${offset}px)`;
      this.updateItemStyles(offset);
      this._loadNearbyImages(offset);
    }

    this.allItems.forEach((item, i) => item.classList.toggle('is-active', i === this.domIndex));
    if (this.viewBtn) this.viewBtn.href = this.products[this.logicalIndex()].url;
  }
}

ShirtCarousel.SHIFT_X = 3; // px right of section center the carousel sits

function initHeadSweat(carousels = []) {
  const wrapper = document.querySelector('.character-head-wrapper');
  const template = document.querySelector('.character-sweat');
  if (!wrapper || !template) return;

  let active = false;
  let timer = null;
  let eyeInterval = null;
  let hoverDelayTimer = null;

  const spawn = () => {
    if (!active) return;
    const drop = document.createElement('img');
    drop.src = template.src;
    drop.className = 'character-sweat-drop';
    drop.style.width = (20 + Math.random() * 10) + 'px';
    drop.style.right = '4px';
    drop.style.top = '4px';
    const dur = 800;
    drop.style.animation = `sc-sweat-fall ${dur}ms forwards`;
    wrapper.appendChild(drop);
    setTimeout(() => {
      drop.remove();
      timer = setTimeout(spawn, 20 + Math.random() * 40);
    }, dur);
  };

  let eyeDir = 1;
  const franticEyes = () => {
    if (!active) return;
    eyeDir *= -1;
    wrapper.style.setProperty('--eye-offset-l', (eyeDir * 3) + 'px');
    wrapper.style.setProperty('--eye-offset-r', (eyeDir * 2.5) + 'px');
    eyeInterval = setTimeout(franticEyes, 90 + Math.random() * 180);
  };

  const activate = () => {
    if (active) return;
    active = true;
    eyeDir = 1;
    wrapper.dataset.hover = '1';
    carousels.forEach(c => c.stopAutoScroll());
    spawn();
    eyeInterval = setTimeout(franticEyes, 150 + Math.random() * 400);
  };

  const deactivate = () => {
    active = false;
    delete wrapper.dataset.hover;
    clearTimeout(timer);
    clearTimeout(eyeInterval);
    wrapper.style.setProperty('--eye-offset-l', '0px');
    wrapper.style.setProperty('--eye-offset-r', '0px');
    carousels.forEach(c => c.startAutoScroll());
  };

  const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

  if (!isTouchDevice) {
    wrapper.addEventListener('mouseenter', () => {
      hoverDelayTimer = setTimeout(activate, 400);
    });

    wrapper.addEventListener('mouseleave', () => {
      clearTimeout(hoverDelayTimer);
      hoverDelayTimer = null;
      const section = wrapper.closest('[data-shirt-carousel-id]');
      if (section?.dataset.headDrag) return;
      deactivate();
    });
  }

  wrapper.addEventListener('sc:drag-start', activate);

  wrapper.addEventListener('sc:drag-end', () => {
    if (isTouchDevice || !wrapper.matches(':hover')) deactivate();
  });
}

function initCharacterBlink() {
  const wrapper = document.querySelector('.character-head-wrapper');
  const eyeEls = document.querySelectorAll('.character-eye:not(.character-eye--blink)');
  const blinkEls = document.querySelectorAll('.character-eye--blink');
  if (!wrapper || !eyeEls.length || !blinkEls.length) return;

  let isLooking = false;

  function doBlink() {
    if (isLooking || wrapper.closest('[data-dizzy]')) { setTimeout(doBlink, 600); return; }
    eyeEls.forEach(el => { el.style.opacity = '0'; });
    blinkEls.forEach(el => { el.style.opacity = '1'; });
    setTimeout(() => {
      blinkEls.forEach(el => { el.style.opacity = '0'; });
      eyeEls.forEach(el => { el.style.opacity = '1'; });
      setTimeout(doBlink, 4000 + Math.random() * 6000);
    }, 100);
  }

  function doLook() {
    if (wrapper.closest('[data-dizzy]')) { setTimeout(doLook, 1000); return; }
    isLooking = true;

    // Look left — right eye (inner) farther, left eye (outer) less
    wrapper.style.setProperty('--eye-offset-l', '-2.5px');
    wrapper.style.setProperty('--eye-offset-r', '-1.5px');
    setTimeout(() => {
      wrapper.style.setProperty('--eye-offset-l', '1.5px');
      wrapper.style.setProperty('--eye-offset-r', '2.5px');
      setTimeout(() => {
        // Return to center
        wrapper.style.setProperty('--eye-offset-l', '0px');
        wrapper.style.setProperty('--eye-offset-r', '0px');
        setTimeout(() => {
          isLooking = false;
          setTimeout(doLook, 5000 + Math.random() * 6000);
        }, 400);
      }, 1400);
    }, 1400);
  }

  setTimeout(doBlink, 3000 + Math.random() * 4000);
  setTimeout(doLook, 4000 + Math.random() * 4000);
}

document.addEventListener('DOMContentLoaded', () => {
  const carousels = [];
  document.querySelectorAll('[data-shirt-carousel-id]').forEach(el => {
    carousels.push(new ShirtCarousel(el.dataset.shirtCarouselId));
  });
  initCharacterBlink();
  initHeadSweat(carousels);

  // Game panel — Web Animations API morph + JS-only fade (no CSS transition)
  document.querySelectorAll('.sc-bb-play-btn').forEach(btn => {
    const section = btn.closest('[data-shirt-carousel-id]');
    const panel   = section?.querySelector('.sc-bb-panel');
    const backBtn = panel?.querySelector('.sc-bb-close-x');
    if (!panel) return;

    const carouselEls = [
      section.querySelector('.shirt-carousel-inner'),
      section.querySelector('.shirt-carousel-stage'),
      section.querySelector('.shirt-carousel-controls'),
    ].filter(Boolean);

    btn.addEventListener('click', () => {
      const sr = section.getBoundingClientRect();
      const br = btn.getBoundingClientRect();
      const sx = window.scrollX, sy = window.scrollY;

      // Flash panel at opacity:0 to get the true rendered frame rect
      panel.style.cssText = 'display:flex; opacity:0; pointer-events:none;';
      void panel.offsetHeight;
      const bbFrame = panel.querySelector('.sc-bb-frame');
      const fr = bbFrame ? bbFrame.getBoundingClientRect() : null;
      panel.style.cssText = '';
      void panel.offsetHeight;

      const sW = sr.width, sH = sr.height;
      const fW = fr ? fr.width  : Math.min((sH - 60) * 0.75, sW - 60);
      const fH = fr ? fr.height : sH - 60;
      const fX = fr ? fr.left + sx : sr.left + (sW - fW) / 2 + sx;
      const fY = fr ? fr.top  + sy : sr.top  + (sH - fH) / 2 + sy;

      // ── Flying head: detaches from carousel character, flies to paddle ──
      const headEl      = section.querySelector('.character-head');
      const headWrapper = section.querySelector('.character-head-wrapper');
      if (headEl && fr) {
        const hr    = headEl.getBoundingClientRect();
        const cH    = Math.max(fr.height - 48, 1); // canvas height minus HUD
        const bR    = Math.round(Math.min(fr.width, cH) * 0.055);
        const endCX = fr.left + fr.width / 2;
        const endCY = fr.top + 48 + (cH - Math.round(cH * 0.06)) - bR - 2;

        if (headWrapper) {
          headWrapper.style.transition = 'opacity 120ms ease';
          headWrapper.style.opacity    = '0';
        }

        const flyHead = document.createElement('img');
        flyHead.src = 'https://cdn.shopify.com/s/files/1/0625/5309/6236/files/HEAD_WITH_EYES.png?v=1779861801';
        flyHead.style.cssText = 'position:fixed;pointer-events:none;z-index:2147483647;';
        flyHead.style.left   = hr.left + 'px';
        flyHead.style.top    = hr.top  + 'px';
        flyHead.style.width  = hr.width  + 'px';
        flyHead.style.height = hr.height + 'px';
        document.body.appendChild(flyHead);

        const flyDur = 820;
        const flyT0  = performance.now();
        const startCX = hr.left + hr.width / 2;
        const startCY = hr.top  + hr.height / 2;

        (function flyLoop(now) {
          const raw = Math.min((now - flyT0) / flyDur, 1);
          const e   = raw < 0.5 ? 2 * raw * raw : -1 + (4 - 2 * raw) * raw;
          const w   = hr.width  + (bR * 2   - hr.width)  * e;
          const h   = hr.height + (bR * 2.1 - hr.height) * e;
          const cx  = startCX + (endCX - startCX) * e;
          const cy  = startCY + (endCY - startCY) * e;
          flyHead.style.left   = (cx - w / 2) + 'px';
          flyHead.style.top    = (cy - h / 2) + 'px';
          flyHead.style.width  = w + 'px';
          flyHead.style.height = h + 'px';
          if (raw < 1) {
            requestAnimationFrame(flyLoop);
          } else {
            flyHead.style.transition = 'opacity 150ms ease';
            flyHead.style.opacity    = '0';
            setTimeout(() => flyHead.remove(), 160);
          }
        }(performance.now()));
      }

      const morph = document.createElement('div');
      morph.style.setProperty('position',       'absolute',                    'important');
      morph.style.setProperty('background',     '#ffffff',                    'important');
      morph.style.setProperty('box-shadow',     'inset 0 0 0 3px #ff278c',    'important');
      morph.style.setProperty('z-index',        '2147483646',                 'important');
      morph.style.setProperty('pointer-events', 'none',                       'important');
      morph.style.setProperty('display',        'block',                      'important');
      morph.style.opacity      = '1';   // no !important — Web Animations must override this
      morph.style.left         = (br.left + sx) + 'px';
      morph.style.top          = (br.top  + sy) + 'px';
      morph.style.width        = br.width  + 'px';
      morph.style.height       = br.height + 'px';
      morph.style.borderRadius = '50px';
      document.body.appendChild(morph);

      const ease = 'cubic-bezier(0.4, 0, 0.2, 1)';

      const p1 = morph.animate([
        { left: (br.left + sx) + 'px', width: br.width + 'px', borderRadius: '50px' },
        { left: fX + 'px',              width: fW + 'px',       borderRadius: '14px' },
      ], { duration: 180, easing: ease, fill: 'forwards' });

      p1.onfinish = () => {
        morph.style.left = fX + 'px';
        morph.style.width = fW + 'px';
        morph.style.borderRadius = '14px';
        p1.cancel();

        setTimeout(() => {
          const p2 = morph.animate([
            { top: (br.top + sy) + 'px', height: br.height + 'px', borderRadius: '14px' },
            { top: fY + 'px',             height: fH + 'px',        borderRadius: '20px' },
          ], { duration: 220, easing: ease, fill: 'forwards' });

          p2.onfinish = () => {
            // Show panel at opacity:0 — no CSS transition involved at all
            section.classList.add('sc-game-active');
            panel.style.cssText = 'display:flex; opacity:0; pointer-events:auto;';
            void panel.offsetHeight;   // commit layout at opacity:0 before any reflow

            carousels.forEach(c => c.stopAutoScroll());
            if (window.BrickBreaker) window.BrickBreaker.startReady();

            const fadeDur = 350;
            const fadeStart = performance.now();

            requestAnimationFrame(function panelFade(now) {
              const t    = Math.min((now - fadeStart) / fadeDur, 1);
              const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
              panel.style.opacity = String(ease);
              carouselEls.forEach(el => { el.style.opacity = String(1 - ease); });
              if (t < 1) {
                requestAnimationFrame(panelFade);
              } else {
                panel.style.opacity = '1';
                carouselEls.forEach(el => { el.style.opacity = '0'; });
                // Panel fully loaded — now fade the pill out
                morph.animate(
                  [{ opacity: 1 }, { opacity: 0 }],
                  { duration: 200, easing: 'ease', fill: 'forwards' }
                ).onfinish = () => {
                  morph.remove();
                  // Read the actual rendered frame rect — more reliable than JS calculations
                  const bbFrame = panel.querySelector('.sc-bb-frame');
                  const fr = bbFrame && bbFrame.getBoundingClientRect();
                  const srNow = section.getBoundingClientRect();
                  const cm = document.createElement('div');
                  cm.style.setProperty('position',       'absolute',   'important');
                  cm.style.setProperty('background',     'rgba(255,39,140,0.28)', 'important');
                  cm.style.setProperty('z-index',        '25',         'important');
                  cm.style.setProperty('pointer-events', 'none',       'important');
                  cm.style.setProperty('display',        'block',      'important');
                  cm.style.opacity      = '1';
                  cm.style.left         = (fr ? fr.left - srNow.left : (sW - fW) / 2) + 'px';
                  cm.style.top          = (fr ? fr.top  - srNow.top  : (sH - fH) / 2) + 'px';
                  cm.style.width        = (fr ? fr.width  : fW) + 'px';
                  cm.style.height       = (fr ? fr.height : fH) + 'px';
                  cm.style.borderRadius = '20px';
                  section.appendChild(cm);
                  closeMorph = cm;
                };
              }
            });
          };
        }, 80);
      };
    });

    let closeMorph = null;

    backBtn?.addEventListener('click', () => {
      if (window.BrickBreaker) window.BrickBreaker.stop();

      const btn    = section.querySelector('.sc-bb-play-btn');
      const bbFrame = panel.querySelector('.sc-bb-frame');
      const sr    = section.getBoundingClientRect();
      const br    = btn?.getBoundingClientRect();
      const fr    = bbFrame?.getBoundingClientRect();
      const fXr   = fr ? fr.left - sr.left : 30;
      const fYr   = fr ? fr.top  - sr.top  : 30;
      const fW    = fr ? fr.width  : sr.width  - 60;
      const fH    = fr ? fr.height : sr.height - 60;

      // Guarantee the morph is visible BEFORE the fade begins —
      // this ensures there's always something behind the panel as it fades out.
      if (!closeMorph) {
        const cm = document.createElement('div');
        cm.style.setProperty('position',       'absolute',                    'important');
        cm.style.setProperty('background',     '#ffffff',                    'important');
        cm.style.setProperty('box-shadow',     'inset 0 0 0 3px #ff278c',    'important');
        cm.style.setProperty('z-index',        '25',                         'important');
        cm.style.setProperty('pointer-events', 'none',                       'important');
        cm.style.setProperty('display',        'block',                      'important');
        cm.style.borderRadius = '20px';
        cm.style.opacity = '1';
        cm.style.left = fXr + 'px'; cm.style.top = fYr + 'px';
        cm.style.width = fW + 'px'; cm.style.height = fH + 'px';
        section.appendChild(cm);
        closeMorph = cm;
      } else {
        closeMorph.style.setProperty('background', '#ffffff',                 'important');
        closeMorph.style.setProperty('box-shadow', 'inset 0 0 0 3px #ff278c','important');
        closeMorph.style.left = fXr + 'px'; closeMorph.style.top  = fYr + 'px';
        closeMorph.style.width = fW + 'px'; closeMorph.style.height = fH + 'px';
        closeMorph.style.opacity = '1';
      }
      const morph = closeMorph;
      closeMorph = null;

      const fadeStart = performance.now();
      const fadeDur   = 400;

      requestAnimationFrame(function panelFade(now) {
        const t    = Math.min((now - fadeStart) / fadeDur, 1);
        const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
        panel.style.opacity = String(1 - ease);
        carouselEls.forEach(el => { el.style.opacity = String(ease); });
        if (t < 1) {
          requestAnimationFrame(panelFade);
        } else {
          panel.style.cssText = '';
          section.classList.remove('sc-game-active');
          carouselEls.forEach(el => { el.style.opacity = ''; });
          const hw = section.querySelector('.character-head-wrapper');
          if (hw) hw.style.opacity = '';
          carousels.forEach(c => c.startAutoScroll());

          if (!br) return;

          // Play button position relative to section
          const brX = br.left - sr.left;
          const brY = br.top  - sr.top;

          const easeStr = 'cubic-bezier(0.4, 0, 0.2, 1)';

          // Phase 1: vertical collapse (after 250ms hold)
          setTimeout(() => {
            morph.style.transition = `top 220ms ${easeStr}, height 220ms ${easeStr}, border-radius 220ms ${easeStr}`;
            void morph.offsetHeight;
            morph.style.top          = brY + 'px';
            morph.style.height       = br.height + 'px';
            morph.style.borderRadius = '14px';
          }, 250);

          // Phase 2: horizontal collapse (after p1 + 80ms pause)
          setTimeout(() => {
            morph.style.transition = `left 180ms ${easeStr}, width 180ms ${easeStr}, border-radius 180ms ${easeStr}`;
            void morph.offsetHeight;
            morph.style.left         = brX + 'px';
            morph.style.width        = br.width + 'px';
            morph.style.borderRadius = '50px';
          }, 250 + 220 + 80);

          // Fade out (after p2 + 500ms hold as pill)
          setTimeout(() => {
            morph.style.transition = 'opacity 200ms ease';
            void morph.offsetHeight;
            morph.style.opacity = '0';
            setTimeout(() => morph.remove(), 220);
          }, 250 + 220 + 80 + 180 + 500);
        }
      });
    });
  });
});
